import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Building2, IndianRupee, MapPin, Calendar, FileText, CheckCircle } from 'lucide-react';

const StartFundingPage = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    orgName: '',
    contactName: '',
    email: '',
    phone: '',
    csrBudgetMin: '',
    csrBudgetMax: '',
    domains: [],
    geographies: [],
    timeline: '',
    summary: ''
  });

  const domainOptions = [
    'Education',
    'Healthcare',
    'Skill Development',
    'Women Empowerment',
    'Rural Development',
    'Environment & Sustainability',
    'Sanitation',
    'Sports',
    'Armed Forces Veterans'
  ];

  const stateOptions = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/xchange/intents/submit-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          budgetMin: parseFloat(formData.csrBudgetMin),
          budgetMax: parseFloat(formData.csrBudgetMax)
        })
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        const error = await response.json();
        alert(error.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-28 pb-16 px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-lg mx-auto mb-6 flex items-center justify-center bg-muted">
              <CheckCircle className="w-8 h-8 text-foreground" />
            </div>
            <h1 className="heading-1 text-foreground mb-4">
              Thank You for Registering!
            </h1>
            <p className="body-large text-muted-foreground mb-8">
              Your CSR intent has been submitted successfully and is now <strong className="text-foreground">pending verification</strong>. Our team will review your submission within 24-48 hours.
            </p>
            <div className="card-base p-6 mb-8 text-left">
              <h3 className="heading-4 text-foreground mb-4">What Happens Next?</h3>
              <ul className="space-y-3 text-foreground">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>1</span>
                  <span><strong>Acknowledgment Email:</strong> You'll receive a confirmation email shortly</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>2</span>
                  <span><strong>Admin Review:</strong> TMG team verifies your details (24-48 hours)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>3</span>
                  <span><strong>Pending Listing:</strong> Your listing appears on Xchange with "Pending Verification" tag</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>4</span>
                  <span><strong>Approval Email:</strong> Once verified, you'll receive confirmation and full listing goes live</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-sm font-bold text-white" style={{background: 'hsl(30 10% 25%)'}}>5</span>
                  <span><strong>Matching Begins:</strong> TMG connects you with relevant NGO projects</span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/explore-projects')} className="btn-primary btn-lg">Browse Available Projects</button>
              <button onClick={() => navigate('/')} className="btn-outline btn-lg">Back to Home</button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4 md:px-8 gradient-warm relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="heading-1 text-foreground mb-4">Start Funding CSR Projects</h1>
          <p className="body-large text-muted-foreground max-w-3xl mx-auto">
            Express your CSR intent and get matched with verified NGOs and implementation agencies across India
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="card-base p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* Company Details */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <Building2 className="w-6 h-6" />
                  Company Details
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label mb-1.5 block">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.orgName}
                      onChange={(e) => setFormData({...formData, orgName: e.target.value})}
                      className="input-base"
                      placeholder="e.g., ABC Private Limited"
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Contact Person <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.contactName}
                      onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                      className="input-base"
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-base"
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div>
                    <label className="label mb-1.5 block">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-base"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                </div>
              </div>

              {/* CSR Budget */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <IndianRupee className="w-6 h-6" />
                  CSR Budget Range
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label mb-1.5 block">
                      Minimum Budget (₹ Lakhs) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.1"
                      value={formData.csrBudgetMin}
                      onChange={(e) => setFormData({...formData, csrBudgetMin: e.target.value})}
                      className="input-base"
                      placeholder="e.g., 25"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{color: '#1E3A5F'}}>
                      Maximum Budget (₹ Lakhs) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.1"
                      value={formData.csrBudgetMax}
                      onChange={(e) => setFormData({...formData, csrBudgetMax: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2"
                      placeholder="e.g., 100"
                    />
                  </div>
                </div>
              </div>

              {/* Domains of Interest */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <FileText className="w-6 h-6" />
                  Domains of Interest <span className="text-red-500">*</span>
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {domainOptions.map((domain) => (
                    <label key={domain} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.domains.includes(domain)}
                        onChange={() => handleCheckboxChange('domains', domain)}
                        className="w-5 h-5 rounded accent-primary"
                      />
                      <span className="text-sm font-medium text-foreground">{domain}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Preferred Geographies */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <MapPin className="w-6 h-6" />
                  Preferred Geographies <span className="text-red-500">*</span>
                </h3>
                <div className="grid md:grid-cols-4 gap-3 max-h-64 overflow-y-auto p-4 bg-muted/30 rounded-lg">
                  {stateOptions.map((state) => (
                    <label key={state} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.geographies.includes(state)}
                        onChange={() => handleCheckboxChange('geographies', state)}
                        className="w-4 h-4 rounded accent-primary"
                      />
                      <span className="text-sm text-foreground">{state}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="heading-3 text-foreground mb-6 flex items-center gap-3">
                  <Calendar className="w-6 h-6" />
                  Project Timeline Preference
                </h3>
                <select
                  required
                  value={formData.timeline}
                  onChange={(e) => setFormData({...formData, timeline: e.target.value})}
                  className="input-base"
                >
                  <option value="">Select Timeline</option>
                  <option value="3-6 months">3-6 months</option>
                  <option value="6-12 months">6-12 months</option>
                  <option value="1-2 years">1-2 years</option>
                  <option value="2+ years">2+ years</option>
                </select>
              </div>

              {/* Summary */}
              <div>
                <label className="label mb-1.5 block">
                  Additional Information (Optional)
                </label>
                <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({...formData, summary: e.target.value})}
                  className="input-base resize-none"
                  rows="4"
                  placeholder="Any specific requirements, focus areas, or preferences..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button type="submit" className="btn-primary flex-1 btn-lg">Submit CSR Intent</button>
                <button type="button" onClick={() => navigate('/')} className="btn-outline btn-lg">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StartFundingPage;
